import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";
import { fmtKwp } from "../lib/format";
import InvestorShares from "./InvestorShares";
import ClassInsightsCard from "./analytics/ClassInsightsCard";
import { CLASSIFICATIONS, COMBINED_META, resolveClass } from "../lib/classification";
import { RISK_STATUSES, resolveRisk } from "../lib/risk";
import RiskBadge from "../components/RiskBadge";
import MarginLeaderboard from "../components/MarginLeaderboard";
import { useAuth } from "../context/AuthContext";
import {
  Loader2, Download, FileText, Zap, AlertTriangle,
  UserCircle2, Clock, TrendingUp, Printer, Wrench,
  Filter, ChevronDown, X as XIcon,
} from "lucide-react";
import DateInput from "../components/DateInput";

/**
 * Investor Dashboard — read-only executive variant of the PM board.
 *
 * Designed for investor reporting sessions: no advance/assign/edit
 * buttons, big-font KPIs, a horizontal stage funnel where bar width is
 * proportional to kWp, plus an SLA-overrun highlights section and a
 * clean Asset Manager rollup. Print-friendly single-column layout.
 */
export default function InvestorDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [state, setState] = useState({ loading: true, data: null });
  const [classFilter, setClassFilter] = useState(() => localStorage.getItem("inv_class") || "combined");
  // Multi-select risk filter — persist as a comma-separated list; empty
  // means "no filter, show everything" (chips render inactive).
  const [riskFilter, setRiskFilter] = useState(() => {
    try {
      const raw = localStorage.getItem("inv_risk") || "";
      const parts = raw.split(",").map(s => s.trim()).filter(Boolean);
      return new Set(parts.filter(k => ["red", "amber", "green"].includes(k)));
    } catch { return new Set(); }
  });
  const persistRiskFilter = (set) => {
    try { localStorage.setItem("inv_risk", [...set].join(",")); } catch { /* private mode */ }
  };
  const toggleRisk = (key) => {
    setRiskFilter(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      persistRiskFilter(next);
      return next;
    });
  };
  const [driftCount, setDriftCount] = useState(0);
  // Portfolio metadata filters — text search on Unit/Property codes,
  // multi-select on the 4 categorical fields. Multi-selects are stored
  // as Sets keyed by field name. Empty = "no filter".
  const [metaFilters, setMetaFilters] = useState(() => {
    try {
      const raw = localStorage.getItem("inv_meta") || "{}";
      const parsed = JSON.parse(raw);
      return {
        unit_code: parsed.unit_code || "",
        property_code: parsed.property_code || "",
        developer: new Set(parsed.developer || []),
        operator: new Set(parsed.operator || []),
        panel_manufacturer: new Set(parsed.panel_manufacturer || []),
        inverter_manufacturer: new Set(parsed.inverter_manufacturer || []),
      };
    } catch {
      return {
        unit_code: "", property_code: "",
        developer: new Set(), operator: new Set(),
        panel_manufacturer: new Set(), inverter_manufacturer: new Set(),
      };
    }
  });
  const [metaOpen, setMetaOpen] = useState(false);
  const persistMetaFilters = (m) => {
    try {
      localStorage.setItem("inv_meta", JSON.stringify({
        unit_code: m.unit_code || "",
        property_code: m.property_code || "",
        developer: [...m.developer],
        operator: [...m.operator],
        panel_manufacturer: [...m.panel_manufacturer],
        inverter_manufacturer: [...m.inverter_manufacturer],
      }));
    } catch { /* private mode */ }
  };
  const toggleMetaValue = (field, value) => {
    setMetaFilters(prev => {
      const next = { ...prev, [field]: new Set(prev[field]) };
      if (next[field].has(value)) next[field].delete(value);
      else next[field].add(value);
      persistMetaFilters(next);
      return next;
    });
  };
  const setMetaText = (field, value) => {
    setMetaFilters(prev => {
      const next = { ...prev, [field]: value };
      persistMetaFilters(next);
      return next;
    });
  };
  const clearAllMeta = () => {
    const cleared = {
      unit_code: "", property_code: "",
      developer: new Set(), operator: new Set(),
      panel_manufacturer: new Set(), inverter_manufacturer: new Set(),
    };
    setMetaFilters(cleared);
    persistMetaFilters(cleared);
  };
  const metaActiveCount =
    (metaFilters.unit_code ? 1 : 0) +
    (metaFilters.property_code ? 1 : 0) +
    metaFilters.developer.size +
    metaFilters.operator.size +
    metaFilters.panel_manufacturer.size +
    metaFilters.inverter_manufacturer.size;

  // ============ Date-range filter (install date OR handover date) ============
  // Stored per-user in localStorage so an operator's saved window persists
  // across reloads. `field` picks the underlying project date attribute.
  const [dateFilter, setDateFilter] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("inv_date_range") || "{}");
      return {
        field: raw.field === "handover_date" ? "handover_date" : "installation_date",
        from: raw.from || "",
        to: raw.to || "",
      };
    } catch {
      return { field: "installation_date", from: "", to: "" };
    }
  });
  const persistDateFilter = (d) => {
    try { localStorage.setItem("inv_date_range", JSON.stringify(d)); } catch { /* private mode */ }
  };
  const dateActive = !!(dateFilter.from || dateFilter.to);
  const clearDateFilter = () => {
    const cleared = { field: dateFilter.field, from: "", to: "" };
    setDateFilter(cleared);
    persistDateFilter(cleared);
  };

  // ============ RAG drill-down slide-over ============
  // null when closed; otherwise "red" / "amber" / "green".
  const [ragDrill, setRagDrill] = useState(null);
  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/pm/dashboard");
      setState({ loading: false, data });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load dashboard");
      setState({ loading: false, data: null });
    }
  };
  // Admin-only: peek at the lifecycle-status drift count so we can show
  // a small banner if any completed project still has an out-of-sync
  // lifecycle_status. Silently ignore 403 for non-admins.
  const loadDrift = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/settings/sanity/lifecycle-mismatch");
      setDriftCount(data.count || 0);
    } catch { /* non-admin or endpoint down — hide silently */ }
  };
  useEffect(() => { load(); loadDrift(); }, [isAdmin]);

  const rawData = state.data;
  const [healthStrip, setHealthStrip] = useState(null); // { rag, rag_delta, top_dev[], top_op[], np_count, np_delta, ... }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/analytics/health-strip");
        if (alive) setHealthStrip(data);
      } catch { /* silent — falls back to client-side memo without deltas */ }
    })();
    return () => { alive = false; };
  }, []);

  // Portfolio IRR / Yield-on-Cost roll-up (admin/manager only).
  const [irrRollup, setIrrRollup] = useState(null);
  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/analytics/portfolio-irr");
        if (alive) setIrrRollup(data);
      } catch { /* silent (403 for non-admins) */ }
    })();
    return () => { alive = false; };
  }, [isAdmin]);
  // counts/kWp based on the class toggle AND the multi-select risk chips
  // AND the collapsible portfolio-metadata filters.
  const data = useMemo(() => {
    if (!rawData) return null;
    const noClassFilter = classFilter === "combined";
    const noRiskFilter = riskFilter.size === 0;
    const noMetaFilter = metaActiveCount === 0;
    const noDateFilter = !dateActive;
    if (noClassFilter && noRiskFilter && noMetaFilter && noDateFilter) return rawData;
    const npStages = new Set(rawData.not_proceeding_stages || []);
    const uc = (metaFilters.unit_code || "").trim().toLowerCase();
    const pc = (metaFilters.property_code || "").trim().toLowerCase();
    const dateField = dateFilter.field;
    const fromMs = dateFilter.from ? Date.parse(dateFilter.from) : null;
    const toMs = dateFilter.to ? Date.parse(dateFilter.to) : null;
    const matches = (p) => {
      if (!noClassFilter && resolveClass(p.business_classification) !== classFilter) return false;
      if (!noRiskFilter && !riskFilter.has(resolveRisk(p.risk_status))) return false;
      if (uc && !String(p.unit_code || "").toLowerCase().includes(uc)) return false;
      if (pc && !String(p.property_code || "").toLowerCase().includes(pc)) return false;
      for (const f of ["developer", "operator", "panel_manufacturer", "inverter_manufacturer"]) {
        if (metaFilters[f].size > 0 && !metaFilters[f].has(p[f] || "")) return false;
      }
      if (!noDateFilter) {
        const raw = p[dateField];
        if (!raw) return false;
        const t = Date.parse(raw);
        if (Number.isNaN(t)) return false;
        if (fromMs != null && t < fromMs) return false;
        if (toMs != null && t > toMs + 86_400_000 - 1) return false; // inclusive end-of-day
      }
      return true;
    };
    const kwpOf = (p) => (npStages.has(p.lifecycle_status) ? 0 : (Number(p.system_size_kwp) || 0));
    const nextGroups = {};
    const stageAgg = new Map();
    let totalProjects = 0, totalKwp = 0;
    for (const [stage, list] of Object.entries(rawData.projects_by_status || {})) {
      const kept = list.filter(matches);
      nextGroups[stage] = kept;
      const kwp = kept.reduce((s, p) => s + kwpOf(p), 0);
      stageAgg.set(stage, { count: kept.length, total_kwp: kwp });
      totalProjects += kept.length;
      totalKwp += kwp;
    }
    const nextStatuses = (rawData.statuses || []).map(s => ({
      ...s,
      count: (stageAgg.get(s.key) || {}).count ?? 0,
      total_kwp: (stageAgg.get(s.key) || {}).total_kwp ?? 0,
    }));
    // Recompute AM rollup filtered to this classification too.
    const amMap = new Map();
    for (const list of Object.values(nextGroups)) {
      for (const p of list) {
        const key = (p.pm_assignees || {}).asset_manager || "__unassigned__";
        const bucket = amMap.get(key) || { asset_manager_id: key === "__unassigned__" ? null : key,
                                             asset_manager_name: "Unassigned", email: "",
                                             project_count: 0, total_kwp: 0 };
        bucket.project_count++;
        bucket.total_kwp += kwpOf(p);
        amMap.set(key, bucket);
      }
    }
    // Enrich AM names from the raw rollup so we keep the friendly labels.
    for (const src of (rawData.by_asset_manager || [])) {
      const key = src.asset_manager_id || "__unassigned__";
      if (amMap.has(key)) {
        const b = amMap.get(key);
        b.asset_manager_name = src.asset_manager_name;
        b.email = src.email || "";
      }
    }
    return {
      ...rawData,
      projects_by_status: nextGroups,
      statuses: nextStatuses,
      portfolio_total_projects: totalProjects,
      portfolio_total_kwp: totalKwp,
      by_asset_manager: [...amMap.values()].sort((a, b) => (b.total_kwp || 0) - (a.total_kwp || 0)),
    };
  }, [rawData, classFilter, riskFilter, metaFilters, metaActiveCount, dateFilter, dateActive]);
  const maxKwp = useMemo(() => {
    if (!data) return 0;
    return Math.max(1, ...data.statuses.map(s => s.total_kwp || 0));
  }, [data]);

  const overrunProjects = useMemo(() => {
    if (!data) return [];
    const out = [];
    for (const [, list] of Object.entries(data.projects_by_status || {})) {
      for (const p of list) if (p.sla_overrun) out.push(p);
    }
    return out.sort((a, b) => (b.days_in_current_stage || 0) - (a.days_in_current_stage || 0));
  }, [data]);

  // Portfolio risk totals — always computed against the class-filtered
  // universe so chip counts stay honest when the class toggle changes,
  // but ignore the risk filter itself (chips otherwise couldn't be reset).
  // Not-Proceeding projects contribute count but zero kWp.
  const riskTotals = useMemo(() => {
    const base = { red: { count: 0, kwp: 0 }, amber: { count: 0, kwp: 0 }, green: { count: 0, kwp: 0 } };
    if (!rawData) return base;
    const npStages = new Set(rawData.not_proceeding_stages || []);
    const lists = rawData.projects_by_status || {};
    for (const list of Object.values(lists)) {
      for (const p of list) {
        if (classFilter !== "combined" && resolveClass(p.business_classification) !== classFilter) continue;
        const key = resolveRisk(p.risk_status);
        base[key].count++;
        base[key].kwp += npStages.has(p.lifecycle_status) ? 0 : (Number(p.system_size_kwp) || 0);
      }
    }
    return base;
  }, [rawData, classFilter]);

  // Flat, class + risk-filtered project list — used to render the
  // portfolio-by-project table (visible only when a risk filter is on
  // OR the class filter is narrowed) so investors can see WHICH sites
  // are Red/Amber at a glance.
  const filteredProjectRows = useMemo(() => {
    if (!data) return [];
    const rows = [];
    for (const [, list] of Object.entries(data.projects_by_status || {})) {
      for (const p of list) rows.push(p);
    }
    return rows.sort((a, b) => {
      // Sort by risk severity first (red > amber > green), then by kWp desc.
      const order = { red: 0, amber: 1, green: 2 };
      const dr = order[resolveRisk(a.risk_status)] - order[resolveRisk(b.risk_status)];
      if (dr !== 0) return dr;
      return (Number(b.system_size_kwp) || 0) - (Number(a.system_size_kwp) || 0);
    });
  }, [data]);

  // ----- Portfolio metadata option lists + rollups --------------------
  // Options for the multi-select dropdowns come from *rawData*
  // (not the filtered view) so users can always broaden their selection.
  const META_FIELDS = [
    { key: "developer",             label: "Developer",             testId: "meta-developer" },
    { key: "operator",              label: "Operator",              testId: "meta-operator" },
    { key: "panel_manufacturer",    label: "Panel Manufacturer",    testId: "meta-panel" },
    { key: "inverter_manufacturer", label: "Inverter Manufacturer", testId: "meta-inverter" },
  ];
  const metaOptions = useMemo(() => {
    const out = { developer: new Set(), operator: new Set(),
                  panel_manufacturer: new Set(), inverter_manufacturer: new Set() };
    if (!rawData) return out;
    for (const list of Object.values(rawData.projects_by_status || {})) {
      for (const p of list) {
        for (const f of Object.keys(out)) {
          const v = (p[f] || "").trim();
          if (v) out[f].add(v);
        }
      }
    }
    // Return sorted arrays for stable render order.
    return Object.fromEntries(Object.entries(out).map(([k, s]) => [k, [...s].sort((a, b) => a.localeCompare(b))]));
  }, [rawData]);

  // Portfolio health strip — mirrors the Monthly Ops PDF headlines so
  // LP sessions can skip flipping to the PDF. Uses the SAME data source
  // as the PDF (all projects on the dashboard), not the filtered slice.
  const portfolioHealthStrip = useMemo(() => {
    if (!rawData) return null;
    const npStages = new Set(rawData.not_proceeding_stages || []);
    const rag = { red: 0, amber: 0, green: 0 };
    const devAgg = new Map();
    const opAgg = new Map();
    let npCount = 0, npKwp = 0;
    for (const list of Object.values(rawData.projects_by_status || {})) {
      for (const p of list) {
        const risk = resolveRisk(p.risk_status);
        if (rag[risk] !== undefined) rag[risk]++;
        const kwp = Number(p.system_size_kwp) || 0;
        if (npStages.has(p.lifecycle_status)) {
          npCount++;
          npKwp += kwp;
          continue;
        }
        for (const [k, agg] of [["developer", devAgg], ["operator", opAgg]]) {
          const v = (p[k] || "").trim();
          if (!v) continue;
          const b = agg.get(v) || { name: v, project_count: 0, total_kwp: 0 };
          b.project_count++;
          b.total_kwp += kwp;
          agg.set(v, b);
        }
      }
    }
    const topDev = [...devAgg.values()].sort((a, b) => b.total_kwp - a.total_kwp).slice(0, 3);
    const topOp = [...opAgg.values()].sort((a, b) => b.total_kwp - a.total_kwp).slice(0, 3);
    return { rag, topDev, topOp, npCount, npKwp };
  }, [rawData]);

  // Rollups — grouped by each categorical portfolio-metadata field.
  // Computed against the *filtered* dataset so users see the rollup for
  // whatever slice they're currently looking at. Not-Proceeding projects
  // contribute count but zero kWp.
  const metaRollups = useMemo(() => {
    const empty = { developer: [], operator: [], panel_manufacturer: [], inverter_manufacturer: [] };
    if (!data) return empty;
    const npStages = new Set(data.not_proceeding_stages || []);
    const buckets = { developer: new Map(), operator: new Map(),
                      panel_manufacturer: new Map(), inverter_manufacturer: new Map() };
    let totalKwp = 0;
    for (const list of Object.values(data.projects_by_status || {})) {
      for (const p of list) {
        const kwp = npStages.has(p.lifecycle_status) ? 0 : (Number(p.system_size_kwp) || 0);
        totalKwp += kwp;
        for (const f of Object.keys(buckets)) {
          const key = (p[f] || "").trim() || "__unassigned__";
          const b = buckets[f].get(key) || { name: key === "__unassigned__" ? "Unassigned" : key,
                                              project_count: 0, total_kwp: 0 };
          b.project_count++;
          b.total_kwp += kwp;
          buckets[f].set(key, b);
        }
      }
    }
    const out = {};
    for (const f of Object.keys(buckets)) {
      out[f] = [...buckets[f].values()]
        .map(b => ({ ...b, share_pct: totalKwp > 0 ? (b.total_kwp / totalKwp) * 100 : 0 }))
        .sort((a, b) => b.total_kwp - a.total_kwp);
    }
    return out;
  }, [data]);

  if (state.loading) {
    return (
      <div className="flex items-center justify-center py-24 muted text-sm gap-2" data-testid="investor-loading">
        <Loader2 size={16} className="animate-spin" /> Loading dashboard…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto print:p-0 print:max-w-none" data-testid="investor-dashboard">
      <div className="print:hidden">
        <InvestorShares />
      </div>
      {/* ============ Header ============ */}
      <div className="mb-8 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="label mb-2">Executive Reporting</div>
          <h1 className="font-display text-4xl font-bold">Reporting Dashboard</h1>
          <p className="muted text-sm mt-2">
            Read-only overview of every project across the lifecycle — designed for board and investor sessions.
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <button
            className="btn-ghost text-xs"
            onClick={() => window.print()}
            data-testid="investor-print"
          >
            <Printer size={13} /> Print
          </button>
          <a href={`${API}/pm/report.csv`} className="btn-ghost text-xs" data-testid="investor-csv">
            <Download size={13} /> CSV
          </a>
          <a href={`${API}/pm/report.pdf`} className="btn-ghost text-xs" data-testid="investor-pdf">
            <FileText size={13} /> PDF
          </a>
        </div>
      </div>

      {/* Admin-only drift banner: legacy status ↔ lifecycle_status mismatch.
          Hidden when count is 0 so the page stays clean for the majority
          of cases. Links straight to Settings where the fix lives. */}
      {isAdmin && driftCount > 0 && (
        <div
          className="mb-6 flex items-center justify-between gap-4 px-4 py-3 border-l-[3px] print:hidden"
          style={{ background: "#FCE9CE", borderLeftColor: "#B8721E", color: "#7A4A0F" }}
          data-testid="investor-drift-banner"
        >
          <div className="flex items-start gap-2 text-sm">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">
                {driftCount} legacy-status mismatch{driftCount === 1 ? "" : "es"}
              </div>
              <div className="text-xs opacity-90">
                Projects marked <code className="font-mono">status=completed</code> whose
                <code className="font-mono"> lifecycle_status</code> hasn&apos;t caught up —
                the counters on this dashboard understate live sites until they&apos;re back-filled.
              </div>
            </div>
          </div>
          <Link
            to="/settings"
            className="btn-primary inline-flex items-center gap-1.5 whitespace-nowrap"
            data-testid="investor-drift-banner-fix"
          >
            <Wrench size={12} /> Fix now
          </Link>
        </div>
      )}

      {/* Portfolio IRR / Yield-on-Cost roll-up */}
      {isAdmin && irrRollup && irrRollup.evaluated_site_count > 0 && (
        <div className="mb-6" data-testid="investor-irr-rollup">
          <div className="label mb-2">Financial returns · fleet</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1 bg-[var(--surface-2)]">
            <div className="bg-white p-5 border border-[var(--line)]" data-testid="irr-fleet-yoc">
              <div className="label">Fleet Yield on Cost</div>
              <div className="font-display text-2xl font-bold mt-1 font-mono">
                {irrRollup.fleet_yield_on_cost_pct == null ? "—" : `${irrRollup.fleet_yield_on_cost_pct}%`}
              </div>
              <div className="text-xs muted mt-1">{irrRollup.recorded_basis_count} on recorded income</div>
            </div>
            <div className="bg-white p-5 border border-[var(--line)]" data-testid="irr-avg-projected">
              <div className="label">Avg projected IRR</div>
              <div className="font-display text-2xl font-bold mt-1 font-mono">
                {irrRollup.avg_projected_irr_pct == null ? "—" : `${irrRollup.avg_projected_irr_pct}%`}
              </div>
              <div className="text-xs muted mt-1">{irrRollup.floor_pct}% hard floor</div>
            </div>
            <div className={`p-5 border ${irrRollup.below_floor_count > 0
              ? "bg-red-50 border-red-200" : "bg-white border-[var(--line)]"}`}
                 data-testid="irr-below-floor">
              <div className="label">Sites below {irrRollup.floor_pct}% IRR</div>
              <div className={`font-display text-2xl font-bold mt-1 font-mono ${
                irrRollup.below_floor_count > 0 ? "text-red-600" : "text-emerald-700"}`}>
                {irrRollup.below_floor_count}
              </div>
              <div className="text-xs muted mt-1">of {irrRollup.evaluated_site_count} live sites</div>
            </div>
            <div className="bg-[var(--ink)] text-white p-5 border border-[var(--line)]"
                 data-testid="irr-status-breakdown">
              <div className="label !text-white/70">Pathway status</div>
              <div className="flex flex-wrap gap-1.5 mt-2 text-xs font-mono">
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200">
                  {irrRollup.status_counts.on_track} on track
                </span>
                <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200">
                  {irrRollup.status_counts.at_risk} at risk
                </span>
                <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-200">
                  {irrRollup.status_counts.below_floor} below
                </span>
              </div>
            </div>
          </div>
          {irrRollup.below_floor_count > 0 && (
            <div className="mt-2 text-xs muted" data-testid="irr-below-floor-list">
              Below floor:{" "}
              {irrRollup.sites.filter((s) => s.status === "below_floor")
                .map((s) => `${s.project_name} (${s.projected_irr_pct ?? "—"}%)`).join(" · ")}
            </div>
          )}
        </div>
      )}

      {isAdmin && <MarginLeaderboard />}

      {/* Business classification toggle — Retro / Development / Customer / Combined */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6" data-testid="inv-class-toggle">
        {[
          { v: "combined", l: COMBINED_META.label, sub: COMBINED_META.sub, tone: COMBINED_META.tone },
          ...CLASSIFICATIONS.map(c => ({ v: c.key, l: c.label, sub: c.sub, tone: c.tone })),
        ].map(opt => {
          const active = classFilter === opt.v;
          // Live totals per option so admins see the split at a glance.
          // Not-Proceeding projects contribute count but not capacity.
          const npStages = new Set(rawData?.not_proceeding_stages || []);
          const totals = (rawData?.projects_by_status
            ? Object.values(rawData.projects_by_status).flat()
            : []
          ).reduce((acc, p) => {
            const cls = resolveClass(p.business_classification);
            const kwp = npStages.has(p.lifecycle_status) ? 0 : (Number(p.system_size_kwp) || 0);
            if (opt.v === "combined" || cls === opt.v) { acc.count++; acc.kwp += kwp; }
            return acc;
          }, { count: 0, kwp: 0 });
          return (
            <button
              key={opt.v}
              onClick={() => { setClassFilter(opt.v); localStorage.setItem("inv_class", opt.v); }}
              className={`p-3 rounded-lg border-2 text-left transition-all ${active ? "shadow-sm" : "bg-white border-[var(--line)] hover:border-[var(--accent)]"}`}
              style={active ? { borderColor: opt.tone, background: `${opt.tone}15` } : undefined}
              data-testid={`inv-class-${opt.v}`}
              aria-pressed={active}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: opt.tone }}>
                    {opt.l}
                  </div>
                  <div className="text-[10px] muted">{opt.sub}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-lg font-bold leading-tight" style={{ color: opt.tone }}>
                    {fmtKwp(totals.kwp)}
                  </div>
                  <div className="text-[10px] muted mt-0.5">{totals.count} project{totals.count === 1 ? "" : "s"}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ============ Portfolio Health Headlines (mirrors PDF) ============ */}
      {portfolioHealthStrip && (
        <section className="mb-6" data-testid="portfolio-health-strip">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={14} style={{ color: "var(--accent-hover)" }} />
            <h2 className="font-display text-sm font-bold uppercase tracking-wider">
              Portfolio health headlines
            </h2>
            <span className="text-[10px] muted">— same view LPs get in the Monthly Ops PDF</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Risk (RAG) card */}
            <div className="border border-[var(--line)] rounded-lg bg-white p-4" data-testid="health-rag-card">
              <div className="label !text-[9px] mb-3">Risk (RAG)</div>
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  { key: "red", label: "Red", tone: "#C0362C" },
                  { key: "amber", label: "Amber", tone: "#B8721E" },
                  { key: "green", label: "Green", tone: "#0F8B84" },
                ].map(r => {
                  const delta = healthStrip?.rag_delta?.[r.key];
                  // Red / Amber going UP is bad; Green going UP is good.
                  const badWhenUp = r.key === "red" || r.key === "amber";
                  return (
                    <span
                      key={r.key}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                      style={{ background: `${r.tone}15`, color: r.tone }}
                      data-testid={`health-rag-${r.key}`}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: r.tone, display: "inline-block" }} />
                      {portfolioHealthStrip.rag[r.key]} {r.label}
                      <DeltaChip value={delta} badWhenUp={badWhenUp} testId={`health-rag-${r.key}-delta`} />
                    </span>
                  );
                })}
              </div>
            </div>
            {/* Top developers */}
            <div className="border border-[var(--line)] rounded-lg bg-white p-4" data-testid="health-dev-card">
              <div className="label !text-[9px] mb-3">Top developers by kWp</div>
              {portfolioHealthStrip.topDev.length === 0 ? (
                <div className="text-xs muted">No developers tagged on projects yet.</div>
              ) : (
                <ol className="space-y-1.5 text-xs">
                  {portfolioHealthStrip.topDev.map((d, i) => {
                    const api = healthStrip?.top_dev?.find(x => x.name === d.name);
                    return (
                      <li key={d.name} className="flex items-center justify-between gap-2" data-testid={`health-dev-${i}`}>
                        <span className="font-medium truncate flex items-center gap-1">
                          <span className="muted mr-1">{i + 1}.</span>{d.name}
                          {api?.is_new && <span className="text-[9px] px-1 py-0.5 rounded" style={{ background: "#0F8B8420", color: "#0F8B84" }} data-testid={`health-dev-${i}-new`}>NEW</span>}
                        </span>
                        <span className="font-mono whitespace-nowrap flex items-center gap-1.5" style={{ color: "var(--accent-hover)" }}>
                          {fmtKwp(d.total_kwp)} · {d.project_count} proj
                          <DeltaChip value={api?.kwp_delta} unit="kWp" testId={`health-dev-${i}-delta`} />
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
            {/* Top operators */}
            <div className="border border-[var(--line)] rounded-lg bg-white p-4" data-testid="health-op-card">
              <div className="label !text-[9px] mb-3">Top operators by kWp</div>
              {portfolioHealthStrip.topOp.length === 0 ? (
                <div className="text-xs muted">No operators tagged on projects yet.</div>
              ) : (
                <ol className="space-y-1.5 text-xs">
                  {portfolioHealthStrip.topOp.map((d, i) => {
                    const apiRow = healthStrip?.top_op?.find(x => x.name === d.name);
                    return (
                      <li key={d.name} className="flex items-center justify-between gap-2" data-testid={`health-op-${i}`}>
                        <span className="font-medium truncate flex items-center gap-1">
                          <span className="muted mr-1">{i + 1}.</span>{d.name}
                          {apiRow?.is_new && <span className="text-[9px] px-1 py-0.5 rounded" style={{ background: "#0F8B8420", color: "#0F8B84" }} data-testid={`health-op-${i}-new`}>NEW</span>}
                        </span>
                        <span className="font-mono whitespace-nowrap flex items-center gap-1.5" style={{ color: "var(--accent-hover)" }}>
                          {fmtKwp(d.total_kwp)} · {d.project_count} proj
                          <DeltaChip value={apiRow?.kwp_delta} unit="kWp" testId={`health-op-${i}-delta`} />
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>
          {portfolioHealthStrip.npCount > 0 && (
            <div
              className="mt-3 px-4 py-2 rounded text-xs flex items-center gap-2"
              style={{ background: "#F4F4F5", borderLeft: "3px solid #71717A", color: "#3F3F46" }}
              data-testid="health-np-callout"
            >
              <span><strong>Not Proceeding:</strong> {portfolioHealthStrip.npCount} project
              {portfolioHealthStrip.npCount === 1 ? "" : "s"} · {fmtKwp(portfolioHealthStrip.npKwp)}{" "}
              <span className="muted">(excluded from installed &amp; pipeline totals)</span></span>
              <DeltaChip value={healthStrip?.np_delta} badWhenUp testId="health-np-delta" />
            </div>
          )}
        </section>
      )}

      {/* ============ Risk (RAG) filter chips ============ */}
      {/* Risk (RAG) filter — multi-select chips. Clicking a chip toggles
          that risk into the filter; empty selection = show all. Counts on
          each chip are class-filtered so users see live totals per bucket.
          A "See list →" link on each chip opens a slide-over with the full
          drill-down for that bucket. */}
      <div className="mb-6" data-testid="inv-risk-toggle">
        <div className="flex items-center justify-between mb-2">
          <div className="label !text-[10px]">Filter by risk (RAG)</div>
          {riskFilter.size > 0 && (
            <button
              type="button"
              onClick={() => { setRiskFilter(new Set()); persistRiskFilter(new Set()); }}
              className="text-[10px] muted hover:underline"
              data-testid="inv-risk-clear"
            >
              Clear filter
            </button>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {RISK_STATUSES.map((r) => {
            const active = riskFilter.has(r.key);
            const totals = riskTotals[r.key] || { count: 0, kwp: 0 };
            return (
              <div
                key={r.key}
                role="button"
                tabIndex={0}
                onClick={() => toggleRisk(r.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleRisk(r.key); }
                }}
                aria-pressed={active}
                className={`p-3 rounded-lg border-2 text-left transition-all cursor-pointer ${
                  active ? "shadow-sm" : "bg-white border-[var(--line)] hover:border-[var(--accent)]"
                }`}
                style={active ? { borderColor: r.tone, background: `${r.tone}15` } : undefined}
                data-testid={`inv-risk-${r.key}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      style={{
                        width: 12, height: 12, background: r.tone,
                        borderRadius: "50%", display: "inline-block", flexShrink: 0,
                      }}
                    />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: r.tone }}>
                        {r.label}
                      </div>
                      <div className="text-[10px] muted">{r.sub}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-lg font-bold leading-tight" style={{ color: r.tone }}>
                      {totals.count}
                    </div>
                    <div className="text-[10px] muted mt-0.5">{fmtKwp(totals.kwp)}</div>
                  </div>
                </div>
                {totals.count > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setRagDrill(r.key); }}
                    className="mt-2 text-[10px] font-semibold uppercase tracking-widest hover:underline"
                    style={{ color: r.tone }}
                    data-testid={`inv-risk-${r.key}-drill`}
                  >
                    See list →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ Date range filter (install / handover) ============ */}
      <div className="mb-6 border border-[var(--line)] rounded-lg bg-white p-4 print:hidden" data-testid="inv-date-range">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter size={13} style={{ color: "var(--accent-hover)" }} />
            <span className="font-semibold text-sm">Date range</span>
            {dateActive && (
              <span
                className="text-[10px] font-mono px-2 py-0.5 rounded"
                style={{ background: "var(--accent-hover)", color: "white" }}
                data-testid="inv-date-active-chip"
              >
                active
              </span>
            )}
          </div>
          {dateActive && (
            <button
              type="button"
              onClick={clearDateFilter}
              className="text-[10px] muted hover:underline"
              data-testid="inv-date-clear"
            >
              Clear
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block">
            <div className="label !text-[10px] mb-1">Field</div>
            <select
              className="input-box w-full"
              value={dateFilter.field}
              onChange={(e) => {
                const next = { ...dateFilter, field: e.target.value };
                setDateFilter(next); persistDateFilter(next);
              }}
              data-testid="inv-date-field"
            >
              <option value="installation_date">Installation date</option>
              <option value="handover_date">Handover date</option>
            </select>
          </label>
          <label className="block">
            <div className="label !text-[10px] mb-1">From (inclusive)</div>
            <DateInput className="input-box w-full"
              value={dateFilter.from}
              onChange={(e) => {
                const next = { ...dateFilter, from: e.target.value };
                setDateFilter(next); persistDateFilter(next);
              }}
              data-testid="inv-date-from"
            />
          </label>
          <label className="block">
            <div className="label !text-[10px] mb-1">To (inclusive)</div>
            <DateInput className="input-box w-full"
              value={dateFilter.to}
              onChange={(e) => {
                const next = { ...dateFilter, to: e.target.value };
                setDateFilter(next); persistDateFilter(next);
              }}
              data-testid="inv-date-to"
            />
          </label>
        </div>
        <div className="text-[10px] muted mt-2">
          Projects without a value for the selected field are excluded when a range is active.
        </div>
      </div>

      {/* ============ More filters (portfolio metadata) ============ */}
      <div className="mb-6 border border-[var(--line)] rounded-lg bg-white print:hidden" data-testid="inv-meta-panel">
        <button
          type="button"
          onClick={() => setMetaOpen(o => !o)}
          className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-[var(--surface-2,#F6F7F9)]"
          data-testid="inv-meta-toggle"
          aria-expanded={metaOpen}
        >
          <div className="flex items-center gap-2">
            <Filter size={13} style={{ color: "var(--accent-hover)" }} />
            <span className="font-semibold text-sm">More filters</span>
            {metaActiveCount > 0 && (
              <span
                className="text-[10px] font-mono px-2 py-0.5 rounded"
                style={{ background: "var(--accent-hover)", color: "white" }}
                data-testid="inv-meta-active-count"
              >
                {metaActiveCount} active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {metaActiveCount > 0 && (
              <span
                role="button"
                tabIndex={0}
                className="text-[10px] muted hover:underline cursor-pointer"
                onClick={(e) => { e.stopPropagation(); clearAllMeta(); }}
                data-testid="inv-meta-clear"
              >
                Clear all
              </span>
            )}
            <ChevronDown
              size={14}
              style={{ transform: metaOpen ? "rotate(180deg)" : "none", transition: "transform 200ms" }}
            />
          </div>
        </button>
        {metaOpen && (
          <div className="border-t border-[var(--line)] p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Text search: unit + property code */}
            <div>
              <div className="label !text-[10px] mb-1">Unit code (contains)</div>
              <input
                className="input-swiss"
                value={metaFilters.unit_code}
                onChange={(e) => setMetaText("unit_code", e.target.value)}
                placeholder="e.g. U-24"
                data-testid="inv-meta-unit-code"
              />
            </div>
            <div>
              <div className="label !text-[10px] mb-1">Property code (contains)</div>
              <input
                className="input-swiss"
                value={metaFilters.property_code}
                onChange={(e) => setMetaText("property_code", e.target.value)}
                placeholder="e.g. PC-01"
                data-testid="inv-meta-property-code"
              />
            </div>
            {META_FIELDS.map(f => (
              <MultiSelectField
                key={f.key}
                label={f.label}
                testId={f.testId}
                options={metaOptions[f.key] || []}
                selected={metaFilters[f.key]}
                onToggle={(v) => toggleMetaValue(f.key, v)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ============ Class insights card (P&L snapshot + YoY) ============ */}
      {/* Same data widget used on /analytics — shows revenue, YTD kWh, YoY
          delta, and a 12-month sparkline per class. The "Filter page"
          button on each column drives the SAME classFilter state as the
          compact toggle above so both stay in sync. */}
      <ClassInsightsCard
        pageFilter={classFilter === "combined" ? null : classFilter}
        onSetPageFilter={(cls) => {
          const next = cls || "combined";
          setClassFilter(next);
          try { localStorage.setItem("inv_class", next); } catch { /* private mode */ }
        }}
      />

      {/* ============ Big totals ============ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <BigStat
          testid="investor-stat-projects"
          label="Total projects"
          value={data.portfolio_total_projects}
        />
        <BigStat
          testid="investor-stat-kwp"
          label="Portfolio capacity"
          value={fmtKwp(data.portfolio_total_kwp)}
          icon={<Zap size={16} />}
          accent
        />
        <BigStat
          testid="investor-stat-overrun"
          label="SLA overruns"
          value={data.portfolio_overrun_count || 0}
          icon={<AlertTriangle size={16} />}
          warn={(data.portfolio_overrun_count || 0) > 0}
        />
        <BigStat
          testid="investor-stat-ams"
          label="Asset Managers"
          value={
            (data.by_asset_manager || []).filter(am => am.asset_manager_id).length
          }
          icon={<UserCircle2 size={16} />}
        />
      </div>

      {/* ============ Stage funnel ============ */}
      <section className="mb-10" data-testid="investor-funnel">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={14} style={{ color: "var(--accent-hover)" }} />
          <h2 className="font-display text-xl font-bold">Lifecycle Funnel</h2>
        </div>
        <div className="text-xs muted mb-4">
          Bar width is proportional to installed capacity (kWp) at each stage.
        </div>
        <div className="border border-[var(--line)] rounded-lg bg-white overflow-hidden">
          {data.statuses.map((s, i) => (
            <FunnelRow key={s.key} stage={s} maxKwp={maxKwp} zebra={i % 2 === 0} />
          ))}
        </div>
      </section>

      {/* ============ Overrun highlights ============ */}
      {overrunProjects.length > 0 && (
        <section className="mb-10" data-testid="investor-overruns">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={14} style={{ color: "#C0362C" }} />
            <h2 className="font-display text-xl font-bold">Projects past SLA</h2>
            <span className="text-xs muted">({overrunProjects.length})</span>
          </div>
          <div className="border border-[var(--line)] rounded-lg bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[var(--line)] bg-[var(--surface-2,#F6F7F9)]">
                  <th className="px-4 py-2 label !text-[9px]">Project</th>
                  <th className="px-4 py-2 label !text-[9px]">Risk</th>
                  <th className="px-4 py-2 label !text-[9px]">Asset Manager</th>
                  <th className="px-4 py-2 label !text-[9px]">Stage</th>
                  <th className="px-4 py-2 label !text-[9px] text-right">In stage</th>
                  <th className="px-4 py-2 label !text-[9px] text-right">SLA</th>
                  <th className="px-4 py-2 label !text-[9px] text-right">Capacity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line-soft,#EEE)]">
                {overrunProjects.map(p => {
                  const stage = data.statuses.find(s => s.key === p.lifecycle_status);
                  return (
                    <tr key={p.id} data-testid={`investor-overrun-row-${p.id}`}>
                      <td className="px-4 py-2 font-medium">{p.name}
                        <div className="text-[11px] muted">{p.customer_name}</div>
                      </td>
                      <td className="px-4 py-2"><RiskBadge value={p.risk_status} size="xs" testId={`investor-overrun-risk-${p.id}`} /></td>
                      <td className="px-4 py-2 text-xs">{p.asset_manager_name || <em className="muted">unassigned</em>}</td>
                      <td className="px-4 py-2 text-xs">{stage?.label || p.lifecycle_status}</td>
                      <td className="px-4 py-2 text-right font-mono" style={{ color: "#C0362C", fontWeight: 600 }}>
                        {Number(p.days_in_current_stage || 0).toFixed(1)}d
                      </td>
                      <td className="px-4 py-2 text-right font-mono muted">{p.stage_sla_days ?? "—"}d</td>
                      <td className="px-4 py-2 text-right font-mono" style={{ color: "var(--accent-hover)" }}>
                        {fmtKwp(p.system_size_kwp || 0)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ============ Projects matching filter (risk OR portfolio-meta) ============ */}
      {(riskFilter.size > 0 || metaActiveCount > 0) && (
        <section className="mb-10" data-testid="investor-risk-projects">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <h2 className="font-display text-xl font-bold">Projects matching filter</h2>
            <span className="text-xs muted">({filteredProjectRows.length})</span>
            <div className="flex items-center gap-1.5 ml-2 flex-wrap">
              {[...riskFilter].map(k => {
                const meta = RISK_STATUSES.find(r => r.key === k);
                return meta ? <RiskBadge key={k} value={k} size="xs" testId={`inv-risk-active-${k}`} /> : null;
              })}
              {metaActiveCount > 0 && (
                <span
                  className="text-[10px] font-mono px-2 py-0.5 rounded"
                  style={{ background: "var(--accent-hover)15", color: "var(--accent-hover)" }}
                  data-testid="inv-meta-active-pill"
                >
                  {metaActiveCount} portfolio filter{metaActiveCount === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>
          {filteredProjectRows.length === 0 ? (
            <div className="border border-[var(--line)] rounded-lg bg-white p-6 text-sm muted text-center" data-testid="investor-risk-empty">
              No projects match this filter selection. Adjust the class, risk, or portfolio filters above to broaden the view.
            </div>
          ) : (
            <div className="border border-[var(--line)] rounded-lg bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-[var(--line)] bg-[var(--surface-2,#F6F7F9)]">
                    <th className="px-4 py-2 label !text-[9px]">Project</th>
                    <th className="px-4 py-2 label !text-[9px]">Risk</th>
                    <th className="px-4 py-2 label !text-[9px]">Unit / Property</th>
                    <th className="px-4 py-2 label !text-[9px]">Developer</th>
                    <th className="px-4 py-2 label !text-[9px]">Operator</th>
                    <th className="px-4 py-2 label !text-[9px]">Panel</th>
                    <th className="px-4 py-2 label !text-[9px]">Inverter</th>
                    <th className="px-4 py-2 label !text-[9px]">Stage</th>
                    <th className="px-4 py-2 label !text-[9px] text-right">Capacity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--line-soft,#EEE)]">
                  {filteredProjectRows.map(p => {
                    const stage = data.statuses.find(s => s.key === p.lifecycle_status);
                    return (
                      <tr key={p.id} data-testid={`investor-risk-row-${p.id}`}>
                        <td className="px-4 py-2 font-medium">
                          {p.name}
                          <div className="text-[11px] muted">{p.customer_name}</div>
                        </td>
                        <td className="px-4 py-2">
                          <RiskBadge value={p.risk_status} size="xs" testId={`investor-risk-badge-${p.id}`} />
                        </td>
                        <td className="px-4 py-2 text-xs font-mono">
                          {p.unit_code || <span className="muted">—</span>}
                          {" / "}
                          {p.property_code || <span className="muted">—</span>}
                        </td>
                        <td className="px-4 py-2 text-xs">{p.developer || <span className="muted">—</span>}</td>
                        <td className="px-4 py-2 text-xs">{p.operator || <span className="muted">—</span>}</td>
                        <td className="px-4 py-2 text-xs">{p.panel_manufacturer || <span className="muted">—</span>}</td>
                        <td className="px-4 py-2 text-xs">{p.inverter_manufacturer || <span className="muted">—</span>}</td>
                        <td className="px-4 py-2 text-xs">{stage?.label || p.lifecycle_status}</td>
                        <td className="px-4 py-2 text-right font-mono" style={{ color: "var(--accent-hover)" }}>
                          {fmtKwp(p.system_size_kwp || 0)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ============ Portfolio-by rollups (Developer/Operator/Panel/Inverter) ============ */}
      <section className="mb-10" data-testid="investor-meta-rollups">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={14} style={{ color: "var(--accent-hover)" }} />
          <h2 className="font-display text-xl font-bold">Portfolio breakdowns</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {META_FIELDS.map(f => (
            <MetaRollupTable
              key={f.key}
              title={`Portfolio by ${f.label}`}
              rows={metaRollups[f.key] || []}
              testId={`rollup-${f.testId}`}
            />
          ))}
        </div>
      </section>

      {/* ============ Asset Manager rollup ============ */}
      <section className="mb-4" data-testid="investor-am-rollup">
        <div className="flex items-center gap-2 mb-3">
          <UserCircle2 size={14} style={{ color: "var(--accent-hover)" }} />
          <h2 className="font-display text-xl font-bold">Portfolio by Asset Manager</h2>
        </div>
        <div className="border border-[var(--line)] rounded-lg bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[var(--line)] bg-[var(--surface-2,#F6F7F9)]">
                <th className="px-4 py-2 label !text-[9px]">Asset Manager</th>
                <th className="px-4 py-2 label !text-[9px]">Email</th>
                <th className="px-4 py-2 label !text-[9px] text-right">Projects</th>
                <th className="px-4 py-2 label !text-[9px] text-right">Capacity</th>
                <th className="px-4 py-2 label !text-[9px] text-right">Share</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--line-soft,#EEE)]">
              {(data.by_asset_manager || []).map(am => {
                const share = data.portfolio_total_kwp
                  ? (am.total_kwp / data.portfolio_total_kwp) * 100
                  : 0;
                return (
                  <tr key={am.asset_manager_id || "__u__"} data-testid={`investor-am-row-${am.asset_manager_id || "unassigned"}`}>
                    <td className="px-4 py-2 font-medium">{am.asset_manager_name || "Unassigned"}</td>
                    <td className="px-4 py-2 text-xs muted">{am.email || "—"}</td>
                    <td className="px-4 py-2 text-right font-mono">{am.project_count}</td>
                    <td className="px-4 py-2 text-right font-mono" style={{ color: "var(--accent-hover)" }}>
                      {fmtKwp(am.total_kwp)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs muted">
                      {share.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="text-[11px] muted mt-4 text-center print:mt-8">
        Snapshot generated {new Date().toLocaleString("en-GB")} · Read-only view · No project edits available on this dashboard.
      </div>

      {ragDrill && (
        <RagDrilldownPanel
          bucketKey={ragDrill}
          rawData={rawData}
          onClose={() => setRagDrill(null)}
        />
      )}
    </div>
  );
}


/* ============================================================
 * RAG drill-down slide-over — full list of projects in a bucket.
 * ============================================================ */
function RagDrilldownPanel({ bucketKey, rawData, onClose }) {
  const meta = RISK_STATUSES.find(r => r.key === bucketKey) || RISK_STATUSES[0];
  const rows = React.useMemo(() => {
    if (!rawData) return [];
    const out = [];
    for (const list of Object.values(rawData.projects_by_status || {})) {
      for (const p of list) {
        if (resolveRisk(p.risk_status) === bucketKey) out.push(p);
      }
    }
    // Sort by kWp desc so the biggest exposures rise to the top.
    return out.sort((a, b) => (Number(b.system_size_kwp) || 0) - (Number(a.system_size_kwp) || 0));
  }, [rawData, bucketKey]);
  const totalKwp = rows.reduce((s, p) => s + (Number(p.system_size_kwp) || 0), 0);

  // Close on Esc.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
      aria-label={`${meta.label} project drill-down`}
      data-testid={`inv-risk-drill-panel-${bucketKey}`}
    >
      <div
        className="flex-1 bg-black/40 print:hidden"
        onClick={onClose}
        data-testid="inv-risk-drill-backdrop"
      />
      <aside
        className="w-full sm:w-[520px] bg-white shadow-xl border-l border-[var(--line)] flex flex-col"
        style={{ minWidth: 0 }}
      >
        <header
          className="px-5 py-4 flex items-center justify-between border-b border-[var(--line)]"
          style={{ background: `${meta.tone}10` }}
        >
          <div>
            <div className="flex items-center gap-2">
              <span
                style={{
                  width: 12, height: 12, background: meta.tone,
                  borderRadius: "50%", display: "inline-block",
                }}
              />
              <div className="font-display text-lg font-bold" style={{ color: meta.tone }}>
                {meta.label} projects
              </div>
            </div>
            <div className="text-xs muted mt-1">
              {rows.length} project{rows.length === 1 ? "" : "s"} · {fmtKwp(totalKwp)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost"
            data-testid="inv-risk-drill-close"
            aria-label="Close drill-down"
          >
            Close
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">
          {rows.length === 0 ? (
            <div className="p-6 text-center muted text-sm">No projects in this bucket.</div>
          ) : (
            <ul className="divide-y divide-[var(--line)]">
              {rows.map((p) => (
                <li
                  key={p.id}
                  className="px-5 py-3 hover:bg-[var(--surface-2,#F6F7F9)]"
                  data-testid={`inv-risk-drill-row-${p.id}`}
                >
                  <Link
                    to={`/projects/${p.id}`}
                    className="font-semibold text-sm hover:underline"
                    onClick={onClose}
                  >
                    {p.name}
                  </Link>
                  <div className="text-[11px] muted mt-0.5 flex items-center flex-wrap gap-x-2 gap-y-0.5">
                    {p.customer_name && <span>{p.customer_name}</span>}
                    {p.postcode && <span>· {p.postcode}</span>}
                    {p.system_size_kwp != null && <span>· {fmtKwp(p.system_size_kwp)}</span>}
                    {p.lifecycle_status && <span>· {String(p.lifecycle_status).replace(/_/g, " ")}</span>}
                    {p.developer && <span>· dev: {p.developer}</span>}
                    {p.operator && <span>· op: {p.operator}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}


/* ============================================================
 * Big KPI card
 * ============================================================ */
function BigStat({ label, value, icon, testid, accent, warn }) {
  return (
    <div
      className="border border-[var(--line)] rounded-lg p-4 bg-white"
      data-testid={testid}
    >
      <div className="label !text-[9px] mb-2 flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div
        className="font-display text-3xl font-bold leading-tight"
        style={{
          color: warn ? "#C0362C" : accent ? "var(--accent-hover)" : "inherit",
        }}
      >
        {value}
      </div>
    </div>
  );
}


/* ============================================================
 * Single row of the horizontal funnel — bar width proportional to kWp.
 * ============================================================ */
function FunnelRow({ stage, maxKwp, zebra }) {
  const barPct = maxKwp > 0 ? Math.min(100, (stage.total_kwp / maxKwp) * 100) : 0;
  const barColor = stage.overrun_count > 0 ? "#F4A25B" : "var(--accent-hover)";
  return (
    <div
      className={`px-4 py-3 grid gap-3 items-center ${zebra ? "bg-[var(--surface-2,#F6F7F9)]" : ""}`}
      style={{ gridTemplateColumns: "220px 1fr 90px 90px 90px" }}
      data-testid={`investor-funnel-row-${stage.key}`}
    >
      <div>
        <div className="font-semibold text-sm">{stage.label}</div>
        {stage.sla_days != null && (
          <div className="text-[10px] muted font-mono">SLA {stage.sla_days}d</div>
        )}
      </div>
      <div className="relative">
        <div
          className="h-6 rounded-r"
          style={{
            width: `${barPct}%`,
            minWidth: stage.total_kwp > 0 ? 4 : 0,
            background: barColor,
            transition: "width 400ms ease",
          }}
        />
      </div>
      <div className="text-right font-mono text-sm">{stage.count}</div>
      <div className="text-right font-mono text-sm" style={{ color: "var(--accent-hover)" }}>
        {fmtKwp(stage.total_kwp)}
      </div>
      <div className="text-right font-mono text-xs">
        {stage.overrun_count > 0 ? (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold"
            style={{ color: "#C0362C", background: "#FCE7E5" }}
          >
            <AlertTriangle size={9} /> {stage.overrun_count}
          </span>
        ) : stage.avg_current_days != null ? (
          <span className="muted inline-flex items-center gap-1">
            <Clock size={9} /> {Number(stage.avg_current_days).toFixed(1)}d
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </div>
    </div>
  );
}



/* ============================================================
 * Multi-select dropdown for the "More filters" panel.
 * Uses a native <details>/<summary> pattern so it works offline of
 * Radix and stays keyboard-accessible without extra deps.
 * ============================================================ */
function MultiSelectField({ label, options, selected, onToggle, testId }) {
  const [open, setOpen] = React.useState(false);
  const count = selected.size;
  return (
    <div className="relative" data-testid={testId}>
      <div className="label !text-[10px] mb-1">{label}</div>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="input-swiss w-full text-left flex items-center justify-between gap-2 cursor-pointer"
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
      >
        <span className="text-sm truncate">
          {count === 0 ? <span className="muted">All ({options.length})</span> :
            count === 1 ? [...selected][0] :
            `${count} selected`}
        </span>
        <ChevronDown size={12} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
      </button>
      {open && (
        <div
          className="absolute z-20 mt-1 w-full max-h-64 overflow-auto border border-[var(--line)] rounded bg-white shadow-lg"
          data-testid={`${testId}-menu`}
        >
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs muted">No values yet — add via project edit.</div>
          ) : (
            options.map(opt => {
              const active = selected.has(opt);
              return (
                <label
                  key={opt}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-[var(--surface-2,#F6F7F9)] cursor-pointer"
                  data-testid={`${testId}-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => onToggle(opt)}
                    className="cursor-pointer"
                  />
                  <span className="truncate">{opt}</span>
                </label>
              );
            })
          )}
          <div className="border-t border-[var(--line)] px-3 py-1.5 text-right">
            <button
              type="button"
              className="text-[10px] muted hover:underline"
              onClick={() => setOpen(false)}
              data-testid={`${testId}-close`}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ============================================================
 * Rollup table — one row per unique tag with count + kWp + share%.
 * ============================================================ */
function MetaRollupTable({ title, rows, testId }) {
  const totalKwp = rows.reduce((s, r) => s + (r.total_kwp || 0), 0);
  return (
    <div className="border border-[var(--line)] rounded-lg bg-white overflow-hidden" data-testid={testId}>
      <div className="px-4 py-2 border-b border-[var(--line)] bg-[var(--surface-2,#F6F7F9)]">
        <div className="font-semibold text-sm">{title}</div>
      </div>
      {rows.length === 0 ? (
        <div className="p-4 text-xs muted text-center" data-testid={`${testId}-empty`}>
          No projects in this slice yet.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-[var(--line)]">
              <th className="px-4 py-2 label !text-[9px]">Name</th>
              <th className="px-4 py-2 label !text-[9px] text-right">Projects</th>
              <th className="px-4 py-2 label !text-[9px] text-right">Capacity</th>
              <th className="px-4 py-2 label !text-[9px] text-right">Share</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--line-soft,#EEE)]">
            {rows.map(r => {
              const safeKey = (r.name || "unassigned").toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <tr key={r.name} data-testid={`${testId}-row-${safeKey}`}>
                  <td className="px-4 py-2 font-medium">{r.name}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.project_count}</td>
                  <td className="px-4 py-2 text-right font-mono" style={{ color: "var(--accent-hover)" }}>
                    {fmtKwp(r.total_kwp)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs muted">
                    {r.share_pct.toFixed(1)}%
                  </td>
                </tr>
              );
            })}
            <tr className="border-t border-[var(--line)] bg-[var(--surface-2,#F6F7F9)]">
              <td className="px-4 py-2 label !text-[9px]">Total</td>
              <td className="px-4 py-2 text-right font-mono">{rows.reduce((s, r) => s + r.project_count, 0)}</td>
              <td className="px-4 py-2 text-right font-mono" style={{ color: "var(--accent-hover)" }}>
                {fmtKwp(totalKwp)}
              </td>
              <td className="px-4 py-2 text-right font-mono text-xs muted">100.0%</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}


/* ============================================================
 * DeltaChip — small ▲/▼ badge that shows "since last month" momentum.
 * `badWhenUp` inverts the colour semantics for metrics where a rise
 * is a negative signal (Red / Amber RAG counts, NP totals).
 * ============================================================ */
function DeltaChip({ value, unit, badWhenUp = false, testId }) {
  if (value === null || value === undefined || value === 0) return null;
  const up = value > 0;
  // For "bad-when-up" metrics, up = red, down = green. For normal metrics
  // (Green RAG count, developer/operator growth), up = green.
  const isGood = badWhenUp ? !up : up;
  const tone = isGood ? "#0F8B84" : "#C0362C";
  const arrow = up ? "\u25B2" : "\u25BC";
  const abs = Math.abs(value);
  const display = unit === "kWp"
    ? `${up ? "+" : "-"}${abs >= 1000 ? (abs / 1000).toFixed(1) + "k" : abs.toFixed(0)}`
    : `${up ? "+" : ""}${value}`;
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[9px] font-mono px-1.5 py-0.5 rounded"
      style={{ background: `${tone}12`, color: tone }}
      title="since last month"
      data-testid={testId}
    >
      <span aria-hidden>{arrow}</span>
      {display}
    </span>
  );
}

